import { useEffect, useState } from 'react';
import s from './attach-file-modal.module.scss';
import { Modal } from '@shared/ui/modal/modal';
import { Tabs, Tab } from '@shared/ui/tabs/tabs';
import { Button } from '@shared/ui/button/button';
import { useAttachFileModal } from '../../model/use-attach-file-modal';
import { MyFilesTab } from './my-files-tab';
import { ComputerTab } from './computer-tab';

const DEFAULT_MAX_FILES = 5;

const isSameFile = (list, item) =>
	list.some((file) => file === item || (file?.id != null && file.id === item?.id));

/**
 * Модалка прикрепления файла: выбор из «Моих файлов» (ранее загруженных)
 * или загрузка с устройства.
 *
 * Открытие: useAttachFileModal().open({
 *   onSelect: (items) => {},  // (File | Attachment)[]
 *   multiple = false,
 *   maxFiles = 5,
 *   imageOnly = false,        // устарело — используйте onlyCategory
 *   onlyCategory,             // 'image' | 'video' — фильтр по категории файла
 *   maxSize,                  // байты, undefined — без лимита
 * })
 */
export const AttachFileModal = () => {
	const { isOpen, close, modalParams } = useAttachFileModal();

	const multiple = Boolean(modalParams?.multiple);
	const maxFiles = modalParams?.maxFiles ?? DEFAULT_MAX_FILES;
	const imageOnly = Boolean(modalParams?.imageOnly);
	const onlyCategory = modalParams?.onlyCategory || (imageOnly ? 'image' : undefined);
	const maxSize = modalParams?.maxSize;

	const [selected, setSelected] = useState([]);

	useEffect(() => {
		if (!isOpen) setSelected([]);
	}, [isOpen]);

	const toggleFile = (item) => {
		setSelected((prev) => {
			if (isSameFile(prev, item)) {
				return prev.filter((file) => !isSameFile([file], item) && file !== item);
			}
			if (!multiple) return [item];
			if (prev.length >= maxFiles) return prev;
			return [...prev, item];
		});
	};

	const removeFile = (item) => {
		setSelected((prev) => prev.filter((file) => file !== item));
	};

	const addFiles = (files) => {
		setSelected((prev) => {
			const free = maxFiles - prev.length;
			return [...prev, ...files.filter((file) => !isSameFile(prev, file)).slice(0, Math.max(free, 0))];
		});
	};

	const confirm = () => {
		if (!selected.length) return;
		modalParams?.onSelect?.(selected);
		close();
	};

	// Каждая вкладка показывает только свои файлы: «Мои файлы» — загруженные
	// Attachment, «С устройства» — локальные File. Лимит maxFiles общий.
	const selectedAttachments = selected.filter((item) => !(item instanceof File));
	const selectedFiles = selected.filter((item) => item instanceof File);

	return (
		<Modal
			isOpen={isOpen}
			close={close}
			title="Прикрепить файл"
			style={{ content: { width: 560, maxWidth: 'calc(100svw - 40px)' } }}
		>
			<div className={s.body}>
				<Tabs
					className={s.tabsFull}
					options={[
						{ key: 'my', label: 'Мои файлы' },
						{ key: 'pc', label: 'С устройства' },
					]}
				>
					<Tab>
						<MyFilesTab
							selected={selectedAttachments}
							multiple={multiple}
							maxFiles={maxFiles - selectedFiles.length}
							onlyCategory={onlyCategory}
							maxSize={maxSize}
							onToggle={toggleFile}
							onDeleted={removeFile}
						/>
					</Tab>
					<Tab>
						<ComputerTab
							selected={selectedFiles}
							multiple={multiple}
							maxFiles={maxFiles - selectedAttachments.length}
							onlyCategory={onlyCategory}
							maxSize={maxSize}
							onAdd={addFiles}
							onRemove={removeFile}
						/>
					</Tab>
				</Tabs>

				<div className={s.footer}>
					{multiple && selected.length > 0 && (
						<span className={s.counter}>
							Выбрано: {selected.length} из {maxFiles}
						</span>
					)}
					<Button
						label="Прикрепить"
						className={s.submitBtn}
						onClick={confirm}
						disabled={!selected.length}
					/>
				</div>
			</div>
		</Modal>
	);
};
